import React, { useCallback, useRef } from 'react';
import { Dialog } from '@material-ui/core';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import { useDialogState } from '../../hooks';
import { KEYBOARD_KEYS } from '../../const';
import UnsavedChangesWarningDialog from '../UnsavedChangesWarningDialog';
import useModalFocusHandler from '../../hooks/useModalFocusHandler';

const defaultCheckIsDirty = () => false;

const RepliconDialog = ({
  children,
  checkIsDirty = defaultCheckIsDirty,
  onClose,
  open,
  enableUnsavedChangesWarning = false,
  trapFocusWithDatePicker = false,
  ...dialogProps
}) => {
  const dialogId = useRef(`dialog-${uuidv4()}`);

  useModalFocusHandler({
    isOpen: open,
    containerQuerySelector: `[data-dialog-id="${dialogId.current}"] .MuiDialog-container`,
    trapFocusWithDatePicker
  });

  const {
    open: isUnsavedChangesDialogOpen,
    closeDialog: closeUnsavedChangesDialog,
    openDialog: openUnsavedChangesDialog
  } = useDialogState(false);

  const handleKeyDown = useCallback(
    event => {
      if (event.key === KEYBOARD_KEYS.ESCAPE) {
        if (enableUnsavedChangesWarning && checkIsDirty()) {
          openUnsavedChangesDialog();
        } else {
          onClose(event);
        }
      }
    },
    [
      enableUnsavedChangesWarning,
      checkIsDirty,
      onClose,
      openUnsavedChangesDialog
    ]
  );

  return (
    <>
      <Dialog
        open={open}
        onKeyDown={handleKeyDown}
        disableEnforceFocus={trapFocusWithDatePicker}
        {...dialogProps}
        data-dialog-id={dialogId.current}
      >
        {children}
      </Dialog>
      {enableUnsavedChangesWarning && isUnsavedChangesDialogOpen && (
        <UnsavedChangesWarningDialog
          closeParent={onClose}
          onClose={closeUnsavedChangesDialog}
          open={isUnsavedChangesDialogOpen}
        />
      )}
    </>
  );
};

RepliconDialog.propTypes = {
  children: PropTypes.node.isRequired,
  checkIsDirty: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  enableUnsavedChangesWarning: PropTypes.bool,
  trapFocusWithDatePicker: PropTypes.bool
};

export default RepliconDialog;
